import { OldFilmFilter as PixiOldFilmFilter } from 'pixi-filters';
import { assertNumberBetween } from '../../validators';
import { Config } from '../../video-data/config';
import type { BaseSettings } from '../TimeAwareBase';
import { Filter } from './Filter';

export class OldFilmFilter extends Filter {
    static readonly NEUTRAL_NOISE = 0;
    static readonly NEUTRAL_NOISE_SIZE = 1;
    static readonly NEUTRAL_VIGNETTING = 0;
    static readonly NEUTRAL_VIGNETTING_ALPHA = 1;
    static readonly NEUTRAL_VIGNETTING_BLUR = 0;

    override _classType = Config.OLD_FILM_FILTER;
    override pixiFilter: PixiOldFilmFilter;

    constructor(settings: BaseSettings) {
        super(settings);
        // The pixi defaults of the old film filter distort the image, but by default we
        // don't want any visual changes when adding a new filter.
        this.pixiFilter = new PixiOldFilmFilter({
            noise: 0,
            scratch: 0,
            sepia: 0,
            vignetting: 0,
            vignettingBlur: 0,
        });
    }
    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'noise',
            'noiseSize',
            'vignetting',
            'vignettingAlpha',
            'vignettingBlur',
        ]);
    }

    /**
     * Noise (0 to 1)
     */
    get noise(): number {
        return this.pixiFilter.noise;
    }

    set noise(value: number) {
        if (value === this.noise) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.pixiFilter.noise = value;
        this.requiresUpdate = true;
    }

    /**
     * Noise size (1 to 10)
     */
    get noiseSize(): number {
        return this.pixiFilter.noiseSize;
    }

    set noiseSize(value: number) {
        if (value === this.noiseSize) {
            return;
        }
        assertNumberBetween(value, 1, 10);
        this.pixiFilter.noiseSize = value;
        this.requiresUpdate = true;
    }

    /**
     * Vignetting (0 to 1)
     */
    get vignetting(): number {
        return this.pixiFilter.vignetting;
    }

    set vignetting(value: number) {
        if (value === this.vignetting) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.pixiFilter.vignetting = value;
        this.requiresUpdate = true;
    }

    /**
     * Vignetting alpha (0 to 1)
     */
    get vignettingAlpha(): number {
        return this.pixiFilter.vignettingAlpha;
    }

    set vignettingAlpha(value: number) {
        if (value === this.vignettingAlpha) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.pixiFilter.vignettingAlpha = value;
        this.requiresUpdate = true;
    }

    /**
     * Vignetting blur (0 to 1)
     */
    get vignettingBlur(): number {
        return this.pixiFilter.vignettingBlur;
    }

    set vignettingBlur(value: number) {
        if (value === this.vignettingBlur) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.pixiFilter.vignettingBlur = value;
        this.requiresUpdate = true;
    }

    get seed(): number {
        return this.pixiFilter.seed;
    }

    set seed(value: number) {
        this.pixiFilter.seed = value;
    }

    override isNeutral(): boolean {
        return (
            this.noise === OldFilmFilter.NEUTRAL_NOISE &&
            this.noiseSize === OldFilmFilter.NEUTRAL_NOISE_SIZE &&
            this.vignetting === OldFilmFilter.NEUTRAL_VIGNETTING &&
            this.vignettingAlpha === OldFilmFilter.NEUTRAL_VIGNETTING_ALPHA &&
            this.vignettingBlur === OldFilmFilter.NEUTRAL_VIGNETTING_BLUR
        );
    }
}
